import { List, Map } from "immutable";
import { isNil } from "lodash";

import { immutableRecords } from "../../../types";
import { calcEventInternalLayout } from "./calcEventInternalLayout";
import { getLayoutConstants } from "./getLayoutConstants";

const _calcClearAllLayoutForEventList = (
  viewId,
  sortedEventIds,
  eventsById,
  ongoingDescendantsSet,
  layoutIds,
  expandedItems,
  unitScale,
  startY,
  fontSize,
  dateForNow,
  showItemCard
) => {
  const layoutIdsSet = layoutIds.toSet();
  const groupIdsSet = sortedEventIds.toSet();
  const layoutConstants = getLayoutConstants(fontSize, showItemCard);
  let lastBottom = startY,
    mostLeft = null,
    mostRight = null,
    mostBottom = startY;

  let positions = {};

  const eventsToLayout = sortedEventIds.filter(id => layoutIdsSet.has(id));

  eventsToLayout.forEach(evId => {
    const ev = eventsById.get(evId);
    const childIds = expandedItems.contains(ev.id) ? ev.children.filter(id => groupIdsSet.has(id)) : List();
    const hasOngoingDescendants = ongoingDescendantsSet.has(ev.id);
    // Work out how this event will be laid out
    let position = calcEventInternalLayout(
      viewId,
      ev,
      unitScale,
      fontSize,
      dateForNow,
      hasOngoingDescendants,
      showItemCard
    );
    // And calculate where it will be placed.
    const top =
      mostRight === null || position.totalRect.left >= mostRight
        ? startY
        : lastBottom + layoutConstants.EVENT_RECT_MARGIN;
    lastBottom = top + position.totalRect.height;
    // If the item is a parent, then we need to calculate a layout for its children also.
    if (childIds.count()) {
      const childrenResult = _calcClearAllLayoutForEventList(
        viewId,
        sortedEventIds,
        eventsById,
        ongoingDescendantsSet,
        childIds,
        expandedItems,
        unitScale,
        lastBottom,
        fontSize,
        dateForNow,
        showItemCard
      );
      // Merge in the children positions...
      positions = Object.assign(positions, childrenResult.positions);
      // And set the bottom much lower...
      lastBottom =
        childrenResult.bounds.bottom +
        layoutConstants.CHILDREN_BORDER_BOTTOM_PADDING +
        layoutConstants.CHILDREN_BORDER_BOTTOM_MARGIN;

      // Extend when the children range exceeding the most range
      if (mostLeft === null || childrenResult.bounds.left < mostLeft) {
        mostLeft = childrenResult.bounds.left;
      }
      if (mostRight === null || childrenResult.bounds.right > mostRight) {
        mostRight = childrenResult.bounds.right;
      }

      const cTop = top + position.markerRelativeRect.top + layoutConstants.MARKER_HEIGHT / 2;
      const cBottom = childrenResult.bounds.bottom + layoutConstants.CHILDREN_BORDER_BOTTOM_PADDING;
      const cLeft =
        Math.min(childrenResult.bounds.left, position.totalRect.left) - layoutConstants.CHILDREN_BORDER_LEFT_PADDING;
      const cRight =
        Math.max(childrenResult.bounds.right, position.totalRect.right) + layoutConstants.CHILDREN_BORDER_RIGHT_PADDING;

      position = position.set(
        "childrenBorderRect",
        immutableRecords.FlatRectRecord({
          left: cLeft,
          top: cTop,
          width: cRight - cLeft,
          height: cBottom - cTop,
          right: cRight,
          bottom: cBottom
        })
      );
    }

    // Extend when the current event exceeding the most range
    if (mostLeft === null || position.totalRect.left < mostLeft) {
      mostLeft = position.totalRect.left;
    }
    if (mostRight === null || position.totalRect.right > mostRight) {
      mostRight = position.totalRect.right;
    }
    if (lastBottom > mostBottom) {
      mostBottom = lastBottom;
    }

    positions[ev.id] = position.setIn(["totalRect", "top"], top).setIn(["totalRect", "bottom"], lastBottom);
  });
  if (mostLeft === null) mostLeft = 0;
  if (mostRight === null) mostRight = 0;

  return {
    positions,
    bounds: immutableRecords.FlatRectRecord({
      left: mostLeft,
      right: mostRight,
      top: startY,
      bottom: mostBottom,
      width: mostRight - mostLeft,
      height: mostBottom - startY
    })
  };
};

export const calcNodeLayoutClearAll = (
  viewId,
  data,
  ongoingDescendantsSet,
  topLevelItems,
  expandedItems,
  unitScale,
  fontSize,
  dateForNow,
  showItemCard
) => {
  const layoutConstants = getLayoutConstants(fontSize, showItemCard);

  const top = 0;
  let groupTop = 0;

  let minLeft = null;
  let maxRight = null;

  const groups = data.groups.map(group => {
    const thisGroupTop = groupTop;

    if (group.showHeader) {
      groupTop += layoutConstants.GROUP_HEADER_HEIGHT;
    }
    groupTop += layoutConstants.GROUP_TOP_PADDING;

    const result = _calcClearAllLayoutForEventList(
      viewId,
      group.eventIds,
      data.eventsById,
      ongoingDescendantsSet,
      topLevelItems,
      expandedItems,
      unitScale,
      groupTop,
      fontSize,
      dateForNow,
      showItemCard
    );

    if (result.bounds.width > 0) {
      if (isNil(minLeft) || result.bounds.left < minLeft) {
        minLeft = result.bounds.left;
      }
      if (isNil(maxRight) || result.bounds.right > maxRight) {
        maxRight = result.bounds.right;
      }
    }

    const groupBottom = result.bounds.bottom + layoutConstants.TIMELINE_BOTTOM_PADDING;

    const groupBounds = result.bounds.merge({
      top: thisGroupTop,
      bottom: groupBottom,
      height: groupBottom - thisGroupTop
    });

    groupTop = groupBottom;

    return group.merge({
      bounds: groupBounds,
      positions: Map(result.positions),
      headerHeight: layoutConstants.GROUP_HEADER_HEIGHT
    });
  });

  const bounds = immutableRecords.FlatRectRecord({
    top,
    bottom: groupTop,
    height: groupTop,
    left: minLeft,
    right: maxRight,
    width: !isNil(minLeft) ? maxRight - minLeft : 0
  });

  return data.merge({
    groups,
    unitScale,
    bounds
  });
};
